import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useColorMode,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getWidgetConfigs } from '../../widgets';
import { useRef } from 'react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import {
  WidgetOptions,
  WidgetType,
  defaultWidgetColor,
  defaultWidgetLightColor,
} from '../types';
import {
  GenericWidgetSettings,
  GenericWidgetSettingsHandle,
} from './GenericWidgetSettings';
import {
  SpacerWidgetSettings,
  SpacerWidgetSettingsHandle,
} from './SpacerWidgetSettings';

interface Props extends SharedDisclosureProps {
  name: string;
  type: WidgetType;
  color: string | null;
  options?: WidgetOptions;
  onUpdate: (name: string, color: string, options?: WidgetOptions) => void;
}

export function EditWidget({
  isOpen,
  onClose,
  name,
  color,
  type,
  options,
  onUpdate,
}: Props) {
  const { t } = useTranslation();
  const configs = getWidgetConfigs(t);
  const { colorMode } = useColorMode();

  const isSpacer = type === WidgetType.Spacer;

  const genericRef = useRef<GenericWidgetSettingsHandle>(null);
  const spacerRef = useRef<SpacerWidgetSettingsHandle>(null);

  function handleUpdate() {
    if (isSpacer) {
      const values = spacerRef.current?.getValues();
      if (!values) return;
      onUpdate(values.customWidgetTitle ?? '', defaultWidgetColor, values);
    } else {
      const values = genericRef.current?.getValues();
      if (!values) return;
      onUpdate(
        values.name,
        values.color ??
          (colorMode === 'dark' ? defaultWidgetColor : defaultWidgetLightColor),
      );
    }
    onClose();
  }

  const spacerOptions =
    options?.type === WidgetType.Spacer ? options : undefined;

  return (
    <Modal
      size={isSpacer ? 'md' : '2xl'}
      isCentered={true}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('widgets.update')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isSpacer ? (
            <SpacerWidgetSettings
              key="spacer-edit"
              ref={spacerRef}
              initialOptions={spacerOptions}
              namePlaceholder={configs[type].label}
            />
          ) : (
            <GenericWidgetSettings
              key="generic-edit"
              ref={genericRef}
              initialName={name}
              initialColor={color}
              namePlaceholder={configs[type].label}
            />
          )}
        </ModalBody>

        <ModalFooter
          backgroundColor="gray.800"
          _light={{ backgroundColor: 'gray.50' }}
          gap={2}
        >
          <Button variant="texas-solid" onClick={handleUpdate}>
            {t('general.update')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
